export const TAIL_CHASE_BOARD_WIDTH = 72;
export const TAIL_CHASE_BOARD_HEIGHT = 42;
export const TAIL_CHASE_TARGET_SCORE = 3;
export const TAIL_CHASE_ROUND_SECONDS = 60;
export const TAIL_CHASE_TICKS_PER_SECOND = 20;
export const TAIL_CHASE_MOVE_EVERY_TICKS = 2;
export const TAIL_CHASE_MAX_TURN_QUEUE = 2;
export const TAIL_CHASE_BASE_CELLS_PER_SECOND = TAIL_CHASE_TICKS_PER_SECOND / TAIL_CHASE_MOVE_EVERY_TICKS;
export const TAIL_CHASE_FAST_CELLS_PER_SECOND = TAIL_CHASE_TICKS_PER_SECOND;
export const TAIL_CHASE_BOOST_MULTIPLIER = 1.6;
export const TAIL_CHASE_BOOST_MILLISECONDS = 1_500;
export const TAIL_CHASE_MAX_MOVES_PER_TICK = 4;

export type TailChaseDirection = 0 | 1 | 2 | 3;
export type TailChaseDirectionInputResult = 'queued' | 'same' | 'reverse' | 'full' | 'inactive';
export type TailChasePhase = 'countdown' | 'round' | 'round-end' | 'complete';

export interface TailChasePoint {
  readonly x: number;
  readonly y: number;
}

export interface TailChaseSimulationPlayer {
  readonly id: string;
  x: number;
  y: number;
  direction: TailChaseDirection;
  alive: boolean;
  movementCredit: number;
  readonly directionQueue: TailChaseDirection[];
}

export interface TailChaseSimulation {
  readonly width: number;
  readonly height: number;
  tick: number;
  readonly cells: Uint8Array;
  readonly players: TailChaseSimulationPlayer[];
}

export interface TailChaseStepResult {
  readonly moved: boolean;
  readonly eliminatedPlayerIds: readonly string[];
  readonly survivorPlayerIds: readonly string[];
  readonly occupiedCells: readonly TailChaseBoardChange[];
}

export interface TailChaseBoardChange {
  readonly index: number;
  readonly owner: number;
}

export interface TailChasePlayerView {
  readonly id: string;
  readonly nickname: string;
  readonly colorIndex: number;
  readonly score: number;
  readonly x: number;
  readonly y: number;
  readonly direction: TailChaseDirection;
  readonly alive: boolean;
  readonly boostAvailable: boolean;
  readonly boostUntil: number;
}

export interface TailChaseView {
  readonly mode: TailChasePhase;
  readonly matchId: string;
  readonly phaseId: number;
  readonly roundId: number;
  readonly tick: number;
  readonly boardWidth: number;
  readonly boardHeight: number;
  readonly board: string | null;
  readonly boardDelta: string;
  readonly resetBoard: boolean;
  readonly speedLevel: 1 | 2;
  readonly targetScore: number;
  readonly deadlineAt: number;
  readonly winnerId: string | null;
  readonly matchWinnerId: string | null;
  readonly players: readonly TailChasePlayerView[];
}

const DIRECTION_VECTORS: Readonly<Record<TailChaseDirection, TailChasePoint>> = {
  0: { x: 1, y: 0 },
  1: { x: 0, y: 1 },
  2: { x: -1, y: 0 },
  3: { x: 0, y: -1 },
};

function spawnFor(index: number, playerCount: number, width: number, height: number): {
  readonly x: number;
  readonly y: number;
  readonly direction: TailChaseDirection;
} {
  const left = Math.floor(width * 0.16);
  const right = width - left - 1;
  const top = Math.floor(height * 0.22);
  const bottom = height - top - 1;
  const middleX = Math.floor(width / 2);
  const middleY = Math.floor(height / 2);
  if (playerCount === 2) {
    return index === 0
      ? { x: left, y: middleY, direction: 0 }
      : { x: right, y: middleY, direction: 2 };
  }
  if (playerCount === 3) {
    return [
      { x: left, y: middleY, direction: 0 as const },
      { x: right, y: middleY, direction: 2 as const },
      { x: middleX, y: bottom, direction: 3 as const },
    ][index];
  }
  return [
    { x: left, y: top, direction: 0 as const },
    { x: right, y: top, direction: 1 as const },
    { x: right, y: bottom, direction: 2 as const },
    { x: left, y: bottom, direction: 3 as const },
  ][index];
}

export function tailChaseCellIndex(x: number, y: number, width: number): number {
  return y * width + x;
}

export function createTailChaseSimulation(
  playerIds: readonly string[],
  width = TAIL_CHASE_BOARD_WIDTH,
  height = TAIL_CHASE_BOARD_HEIGHT,
): TailChaseSimulation {
  const cells = new Uint8Array(width * height);
  const players = playerIds.map((id, index) => {
    const spawn = spawnFor(index, playerIds.length, width, height);
    cells[tailChaseCellIndex(spawn.x, spawn.y, width)] = index + 1;
    return {
      id,
      ...spawn,
      alive: true,
      movementCredit: 0,
      directionQueue: [],
    };
  });
  return { width, height, tick: 0, cells, players };
}

export function tailChaseOppositeDirection(direction: TailChaseDirection): TailChaseDirection {
  return ((direction + 2) % 4) as TailChaseDirection;
}

export function queueTailChaseDirection(
  simulation: TailChaseSimulation,
  playerId: string,
  direction: TailChaseDirection,
): TailChaseDirectionInputResult {
  const player = simulation.players.find((candidate) => candidate.id === playerId);
  if (!player?.alive) return 'inactive';
  const expectedDirection = player.directionQueue.at(-1) ?? player.direction;
  if (direction === expectedDirection) return 'same';
  if (direction === tailChaseOppositeDirection(expectedDirection)) return 'reverse';
  if (player.directionQueue.length >= TAIL_CHASE_MAX_TURN_QUEUE) return 'full';
  player.directionQueue.push(direction);
  return 'queued';
}

export function stepTailChaseSimulation(
  simulation: TailChaseSimulation,
  cellsPerSecond: number | Readonly<Record<string, number>> = TAIL_CHASE_BASE_CELLS_PER_SECOND,
): TailChaseStepResult {
  simulation.tick += 1;
  for (const player of simulation.players) {
    if (!player.alive) continue;
    const speed = typeof cellsPerSecond === 'number'
      ? cellsPerSecond
      : cellsPerSecond[player.id] ?? TAIL_CHASE_BASE_CELLS_PER_SECOND;
    player.movementCredit = Math.min(
      TAIL_CHASE_MAX_MOVES_PER_TICK,
      player.movementCredit + Math.max(0, speed) / TAIL_CHASE_TICKS_PER_SECOND,
    );
  }

  let moved = false;
  const eliminatedAcrossTick = new Set<string>();
  const occupiedCells: TailChaseBoardChange[] = [];
  for (let substep = 0; substep < TAIL_CHASE_MAX_MOVES_PER_TICK; substep += 1) {
    const movers = simulation.players.filter((player) => player.alive && player.movementCredit >= 1);
    if (movers.length === 0) break;
    moved = true;
    const proposals = new Map<string, TailChasePoint>();
    const eliminated = new Set<string>();
    for (const player of movers) {
      player.movementCredit -= 1;
      const queued = player.directionQueue.shift();
      if (queued !== undefined) player.direction = queued;
      const vector = DIRECTION_VECTORS[player.direction];
      const proposal = { x: player.x + vector.x, y: player.y + vector.y };
      proposals.set(player.id, proposal);
      if (proposal.x < 0 || proposal.x >= simulation.width
        || proposal.y < 0 || proposal.y >= simulation.height
        || simulation.cells[tailChaseCellIndex(proposal.x, proposal.y, simulation.width)] !== 0) {
        eliminated.add(player.id);
      }
    }

    const proposalGroups = new Map<string, string[]>();
    for (const [playerId, proposal] of proposals) {
      const key = `${proposal.x},${proposal.y}`;
      const group = proposalGroups.get(key) ?? [];
      group.push(playerId);
      proposalGroups.set(key, group);
    }
    for (const group of proposalGroups.values()) {
      if (group.length > 1) for (const playerId of group) eliminated.add(playerId);
    }

    for (const player of movers) {
      if (eliminated.has(player.id)) {
        player.alive = false;
        player.movementCredit = 0;
        player.directionQueue.splice(0);
        eliminatedAcrossTick.add(player.id);
        continue;
      }
      const proposal = proposals.get(player.id)!;
      player.x = proposal.x;
      player.y = proposal.y;
      const owner = simulation.players.findIndex((candidate) => candidate.id === player.id) + 1;
      const index = tailChaseCellIndex(player.x, player.y, simulation.width);
      simulation.cells[index] = owner;
      occupiedCells.push({ index, owner });
    }
    if (simulation.players.filter((player) => player.alive).length <= 1) break;
  }

  return {
    moved,
    eliminatedPlayerIds: [...eliminatedAcrossTick],
    survivorPlayerIds: simulation.players.filter((player) => player.alive).map((player) => player.id),
    occupiedCells,
  };
}

export function encodeTailChaseBoard(cells: Uint8Array): string {
  let result = '';
  const chunkSize = 1_024;
  for (let index = 0; index < cells.length; index += chunkSize) {
    result += Array.from(cells.subarray(index, index + chunkSize), (value) => String(value)).join('');
  }
  return result;
}

export function encodeTailChaseBoardDelta(changes: readonly TailChaseBoardChange[]): string {
  return changes.map((change) => `${change.index.toString(36).padStart(3, '0')}${change.owner}`).join('');
}

export function applyTailChaseBoardDelta(
  board: string,
  delta: string,
  boardLength: number,
): string {
  const cells = Array.from(board.padEnd(boardLength, '0').slice(0, boardLength));
  for (let offset = 0; offset + 3 < delta.length; offset += 4) {
    const index = Number.parseInt(delta.slice(offset, offset + 3), 36);
    const owner = delta[offset + 3];
    if (Number.isSafeInteger(index) && index >= 0 && index < cells.length && /^[1-4]$/.test(owner)) {
      cells[index] = owner;
    }
  }
  return cells.join('');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isPlayerView(value: unknown): value is TailChasePlayerView {
  return isRecord(value)
    && typeof value.id === 'string'
    && typeof value.nickname === 'string'
    && Number.isSafeInteger(value.colorIndex)
    && Number.isSafeInteger(value.score)
    && Number.isSafeInteger(value.x)
    && Number.isSafeInteger(value.y)
    && (value.direction === 0 || value.direction === 1 || value.direction === 2 || value.direction === 3)
    && typeof value.alive === 'boolean'
    && typeof value.boostAvailable === 'boolean'
    && typeof value.boostUntil === 'number'
    && Number.isFinite(value.boostUntil);
}

export function isTailChaseView(value: unknown): value is TailChaseView {
  if (!isRecord(value)
    || (value.mode !== 'countdown' && value.mode !== 'round' && value.mode !== 'round-end' && value.mode !== 'complete')
    || typeof value.matchId !== 'string'
    || !Number.isSafeInteger(value.phaseId)
    || !Number.isSafeInteger(value.roundId)
    || !Number.isSafeInteger(value.tick)
    || !Number.isSafeInteger(value.boardWidth)
    || !Number.isSafeInteger(value.boardHeight)
    || (value.board !== null && (typeof value.board !== 'string'
      || value.board.length !== Number(value.boardWidth) * Number(value.boardHeight)
      || !/^[0-4]*$/.test(value.board)))
    || typeof value.boardDelta !== 'string'
    || !/^(?:[0-9a-z]{3}[1-4])*$/.test(value.boardDelta)
    || typeof value.resetBoard !== 'boolean'
    || (value.speedLevel !== 1 && value.speedLevel !== 2)
    || !Number.isSafeInteger(value.targetScore)
    || typeof value.deadlineAt !== 'number'
    || !Number.isFinite(value.deadlineAt)
    || (value.winnerId !== null && typeof value.winnerId !== 'string')
    || (value.matchWinnerId !== null && typeof value.matchWinnerId !== 'string')
    || !Array.isArray(value.players)
    || !value.players.every(isPlayerView)) {
    return false;
  }
  return true;
}
